import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography
} from "@mui/material";
import { PictureAsPdf } from "@mui/icons-material";
import {
  CommentDto,
  OperationalReadiness,
  Signatory,
  SignOff,
  VpDto
} from "../../../api/Api";
import { useRoles } from "../../../auth/useRoles";
import { useIsEntityMerger } from "../../../auth/entityMergerContext";
import {
  canManageSignOff,
  canParticipateInSignOff
} from "../../../auth/permissions";
import { getCurrentCommentAuthor } from "../../../utils/commentUtils";
import { CommentsSection } from "../commentsSection";
import { SignOffSignersPanel } from "./signOffSignersPanel";
import { isSignatoryEmail } from "./signOffCeo";
import { OperationalReadinessSection } from "./operationalReadinessSection";
import {
  isReadinessBlockingSignOff,
  isReadinessSignatoryEmail
} from "./operationalReadiness";
import dayjs from "dayjs";

type PersistHandlers = {
  onError: (error: Error) => void;
  onSuccess: () => void;
};

type Props = {
  /** Name of the entity being signed off, shown in the signers panel. */
  entityName?: string | null;
  /** The stored sign-off. Local optimistic signatures are layered on top of it here. */
  signOff?: SignOff | null;
  /**
   * The entity's VP, proposed as the first signatory on a fresh draft. The VP and the person who
   * signs are the same person in all three books.
   */
  vp?: VpDto | null;
  saving: boolean;
  /**
   * Writes the sign-off back. Each book owns its own mutation, cache seeding and invalidation, so
   * this component never touches React Query directly. Call the handlers so the page can clear
   * its optimistic state or surface the error.
   */
  persist: (signOff: SignOff, handlers: PersistHandlers) => void;
  /**
   * The read-only left column. Per book, since the field set differs entirely. Pass a function to
   * receive extra sections to render in the lifecycle grid (the operational readiness tile).
   */
  detailsPanel: ReactNode | ((extraSections: ReactNode) => ReactNode);
  /**
   * Builds and saves the PDF. Receives the displayed sign-off (stored plus any unsaved local
   * signatures) so the download matches what is on screen.
   */
  onDownloadPdf: (displayedSignOff?: SignOff | null) => void;
  /**
   * Shows the Operational readiness tile (ADHO-551) in the details grid (needs a function
   * `detailsPanel`), which blocks the VP send
   * while it is switched on and unsigned. Counterparties and brokers only.
   */
  withOperationalReadiness?: boolean;
};

/**
 * The sign-off page shared by counterparties, brokers and countries: two columns, signers on the
 * right, comment thread and PDF button below them. Everything book-specific arrives as a prop.
 *
 * It was two identical copies before brokers needed one. Same rule as commentsCard and
 * signOffSignersPanel: do not fork it, add a prop.
 */
export const SignOffPageLayout: FC<Props> = ({
  entityName,
  signOff,
  vp,
  saving,
  persist,
  detailsPanel,
  onDownloadPdf,
  withOperationalReadiness = false
}) => {
  const roles = useRoles();
  const isMerger = useIsEntityMerger();
  const canManage = canManageSignOff(roles, isMerger);
  const currentUserEmail = getCurrentCommentAuthor();
  const isSignatory = isSignatoryEmail(signOff, currentUserEmail);
  const isReadinessSignatory =
    withOperationalReadiness &&
    isReadinessSignatoryEmail(signOff, currentUserEmail);
  // Reject + comments are visible and writable to the signatory VPs plus Market Access / Admin
  // (and Compliance on a merger). The comments also reach the operational readiness signers;
  // Reject stays VP only (the signers panel matches on the VP signatory list).
  const canParticipate = canParticipateInSignOff(
    roles,
    isSignatory || isReadinessSignatory,
    isMerger
  );
  const readinessBlocksSend =
    withOperationalReadiness && isReadinessBlockingSignOff(signOff);

  const [localSignatures, setLocalSignatures] = useState<Signatory[] | null>(
    null
  );
  const [currentComment, setCurrentComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLocalSignatures(null);
  }, [signOff?.status]);

  // Memoised because the signers panel seeds its state from this on mount.
  const vpSignatories = useMemo<Signatory[]>(() => {
    if (!vp?.email) return [];
    return [
      {
        name: vp.displayName ?? vp.email,
        email: vp.email,
        order: 0,
        isCeo: false,
        signed: false
      }
    ];
  }, [vp]);

  const displayedSignOff: SignOff | null | undefined = localSignatures
    ? {
        ...(signOff as SignOff),
        signatories: localSignatures
      }
    : signOff;

  const save = (next: SignOff) => {
    setError(null);
    persist(next, {
      onError: (e) => setError(e.message || "Failed to save sign off."),
      onSuccess: () => setLocalSignatures(null)
    });
  };

  const handleSignCheckboxChange = (
    signatoryEmail: string,
    signed: boolean
  ) => {
    const base = displayedSignOff?.signatories ?? [];
    const next = base.map((s) =>
      s.email === signatoryEmail
        ? {
            ...s,
            signed,
            signedBy: signed ? currentUserEmail : null,
            signedAt: signed ? dayjs().toISOString() : null
          }
        : s
    );
    setLocalSignatures(next);
  };

  const handleSaveSignatures = () => {
    if (!localSignatures || !signOff) return;
    save({ ...signOff, signatories: localSignatures });
  };

  const handleReject = (reason: string) => {
    if (!signOff) return;
    save({
      ...signOff,
      status: "rejected",
      rejectedBy: currentUserEmail,
      rejectionReason: reason
    });
  };

  const clearedSignatures = () =>
    (signOff?.signatories ?? []).map((s) => ({
      ...s,
      signed: false,
      signedBy: null,
      signedAt: null
    }));

  const handleResend = () => {
    if (!signOff) return;
    // Restart cleanly: clear every signature and the rejection so signing begins again.
    // Status flips back to in_progress, which re-triggers invites on the backend.
    save({
      ...signOff,
      status: "in_progress",
      signatories: clearedSignatures(),
      rejectedBy: null,
      rejectedAt: null,
      rejectionReason: null
    });
  };

  const handleWithdraw = () => {
    if (!signOff) return;
    // Pull the sign off back to draft so Market Access can reconfigure and re-send.
    // Clear every signature and any rejection so it reopens cleanly in the configure view.
    save({
      ...signOff,
      status: "draft",
      signatories: clearedSignatures(),
      rejectedBy: null,
      rejectedAt: null,
      rejectionReason: null
    });
  };

  const handleSaveReadiness = (operationalReadiness: OperationalReadiness) => {
    // Readiness can be set up before the VP sign-off has ever been saved, so seed an empty draft.
    // An empty signatory list still lets the signers panel propose the entity's VP.
    save({
      status: "draft",
      signatories: [],
      createdBy: currentUserEmail,
      createdAt: dayjs().toISOString(),
      ...signOff,
      operationalReadiness
    });
  };

  const handleAddSignOffComment = (comment: CommentDto) => {
    if (!signOff || !canParticipate) return;
    save({ ...signOff, comments: [...(signOff.comments ?? []), comment] });
    setCurrentComment("");
  };

  const handleDeleteSignOffComment = (timestamp: string) => {
    if (!signOff || !canParticipate) return;
    save({
      ...signOff,
      comments: (signOff.comments ?? []).filter(
        (c) => c?.timestamp !== timestamp
      )
    });
  };

  return (
    <Box
      sx={{
        padding: 1,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%"
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          flex: 1,
          overflow: { xs: "auto", md: "hidden" }
        }}
      >
        {/*
          On phones the signers panel goes first so a VP opening the invite lands on the Sign
          button, then the details, comments and PDF. The right column uses display: contents
          there so its children can be ordered around the details column.
        */}
        <Box
          sx={{
            flex: { xs: "1 1 auto", md: "1 1 70%" },
            overflowY: { xs: "visible", md: "auto" },
            order: { xs: 1, md: 0 }
          }}
        >
          {typeof detailsPanel === "function"
            ? detailsPanel(
                withOperationalReadiness && (
                  <OperationalReadinessSection
                    signOff={signOff}
                    currentUserEmail={currentUserEmail}
                    entityName={entityName}
                    canManage={canManage}
                    saving={saving}
                    onSave={handleSaveReadiness}
                  />
                )
              )
            : detailsPanel}
        </Box>
        <Box
          sx={{
            display: { xs: "contents", md: "block" },
            flex: { md: "0 0 30%" },
            overflowY: { md: "auto" },
            minWidth: { md: 320 }
          }}
        >
          <SignOffSignersPanel
            signOff={displayedSignOff}
            sendBlockedReason={
              readinessBlocksSend
                ? "Operational readiness must be signed before sending for sign off."
                : null
            }
            persistedSignatories={signOff?.signatories ?? []}
            currentUserEmail={currentUserEmail}
            entityName={entityName}
            defaultSignatories={vpSignatories}
            canManage={canManage}
            saving={saving}
            onSaveDraft={save}
            onSendForSignOff={save}
            onSignCheckboxChange={handleSignCheckboxChange}
            onSaveSignatures={handleSaveSignatures}
            onReject={handleReject}
            onResend={handleResend}
            onWithdraw={handleWithdraw}
          />
          {canParticipate && signOff && (
            <Box padding={1} sx={{ order: { xs: 2, md: 0 } }}>
              <Card elevation={3}>
                <CardContent
                  sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  <Typography variant="h6">Sign off comments</Typography>
                  <CommentsSection
                    comments={(signOff.comments ?? []).filter(
                      (c): c is CommentDto => Boolean(c?.comment)
                    )}
                    currentComment={currentComment}
                    onCurrentCommentChange={setCurrentComment}
                    loading={saving}
                    saveComment={handleAddSignOffComment}
                    onDeleteComment={handleDeleteSignOffComment}
                  />
                </CardContent>
              </Card>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "stretch", md: "flex-end" },
              padding: 1,
              order: { xs: 3, md: 0 }
            }}
          >
            <Button
              startIcon={<PictureAsPdf />}
              variant="outlined"
              onClick={() => onDownloadPdf(displayedSignOff)}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              Download full details summary pdf
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
